<template>
  <define-disclaimer>
    <div
      v-if="action === 'signup' && $t.mfa.disclaimer"
      class="mx-a max-w-70 text-center caption-2"
    >
      <form-legal-disclaimer :content="$t.mfa.disclaimer" />
    </div>
  </define-disclaimer>

  <define-resend>
    <div v-if="!disableResend" class="mt-6 text-center" :class="brandClasses.otpResend">
      <transition mode="out-in" name="fade">
        <span v-if="codeResent" class="fw-bold">{{ $t.mfa.codeResent }}</span>
        <span v-else-if="resendCountdown?.remaining.value">
          {{ replaceAll($t.mfa.otpResendCountdown, { seconds: resendCountdown.remaining.value }) }}
        </span>
        <span v-else>
          {{ $t.mfa.didntReceiveCode }}
          <vf-link class="fw-bold" @click="resendOtp">
            {{ $t.mfa.resend }}
          </vf-link>
        </span>
      </transition>
      <div v-if="otpResendHelpLink && hasResentCode" class="mt-4">
        <vf-link target="_blank" to="/help/contact-us">
          {{ $t.mfa.otpCustomerService }}
        </vf-link>
      </div>
    </div>
  </define-resend>

  <vf-notification v-if="passkeyError" type="error">
    {{ $t.mfa.passkeyError }}
  </vf-notification>

  <base-form ref="formRef" class="f-col grow" :class="{ 'pb-10': !postalCodeRequired }" :form @submit.prevent="handleVerify">
    <label
      v-if="!otpHideLabel"
      class="fw-bold"
      :for="id"
    >
      {{ replaceAll($t.mfa.enterNumDigitCode, { digits }) }}
    </label>
    <pin-input-root
      v-model="otpDigits"
      :id
      class="flex center gap-2.5"
      :class="{ 'mt-3': !otpHideLabel, 'mt-10': otpHideLabel }"
      otp
      required
      type="number"
    >
      <pin-input-input
        v-for="(key, index) in digits"
        :key
        class="aspect-square b b-grey-50 rounded-xs text-center text-base"
        :class="[
          brandClasses.otpInput,
          {
            'b-red-30 c-red-30': error,
            [brandClasses.otpInputPopulated!]: completedDigits.includes(index),
          },
        ]"
        :index
      />
    </pin-input-root>

    <vf-form-error v-if="error" class="mt-2 text-base">
      {{ $t.mfa.incorrectCode }}
    </vf-form-error>

    <template v-if="postalCodeRequired">
      <resend v-if="!otpShowResendAfterCta" />

      <vf-form-field
        v-slot="{ attrs }"
        class-wrapper="b-t b-brand-7 mt-6 pt-10"
        name="postalCode"
        :rule="[...getValidator('postalCode', $t.zipCode)]"
      >
        <input-postal-code
          v-model="form.postalCode"
          v-bind="{
            ...attrs,
            ...($feature.enableMaskedPostalCode && postalCodeFormat.mask ? postalCodeFormat : {}),
          }"
          required
        />
      </vf-form-field>
    </template>

    <vf-form-field
      v-if="action === 'signup'"
      class-wrapper="mt-10"
      name="acceptedTerms"
      :rule="validateRequired($t.validators.requiredCheckbox)"
      v-slot="{ attrs }"
    >
      <vf-checkbox
        v-model="form.acceptedTerms"
        class="items-center ws-pre-wrap pb-2"
        :label-class="brandClasses.otpLegalCheckboxLabel"
        v-bind="attrs"
        required
      >
        <form-legal-disclaimer
          :content="$t.brandAgreement.socialTermsAndConditions || $t.brandAgreement.termsConditionsAndPolicy"
        />
      </vf-checkbox>
    </vf-form-field>

    <vf-button class="mt-6 w-full" :disabled="!isComplete" :loading type="submit">
      {{ $t.mfa.verify }}
    </vf-button>

    <resend v-if="otpShowResendAfterCta" class="o:mt-4" />

    <template v-if="postalCodeRequired">
      <disclaimer class="mt-2" />
    </template>

    <div v-if="action === 'login'" class="mt-8 flex justify-center" :class="{ 'mt-a': otpShowResendAfterCta }">
      <vf-link class="text-center fw-bold" @click="emit('tryAnotherWay')">
        {{ $t.mfa.tryAnotherWay }}
      </vf-link>
    </div>
  </base-form>

  <template v-if="!postalCodeRequired">
    <resend v-if="!otpShowResendAfterCta" />
    <disclaimer class="pt-2 !mt-a" />
  </template>
</template>

<script lang="ts" setup>
import type { MfaData, MfaPayload } from '#root/api/clients/authentication/data-contracts'
import type { FormLocation } from '#types/gtm'
import { EnrollmentState } from '#root/enums/profile'
import { PinInputInput, PinInputRoot } from 'reka-ui'

const { action = 'login', method = 'email', email, digits = 6, formLocation = 'modal:single:none:none' } = defineProps<{
  action?: 'login' | 'signup'
  disableResend?: boolean
  method?: MfaPayload['authMethod']
  email: string
  formLocation?: FormLocation
  digits?: number
}>()

const emit = defineEmits<{ continue: [state: EnrollmentState], resolve: [], tryAnotherWay: [] }>()

const [DefineDisclaimer, Disclaimer] = createReusableTemplate()
const [DefineResend, Resend] = createReusableTemplate()

const otpDigits = ref<number[]>([])
const loading = ref(false)
const error = ref(false)
const formRef = ref()
const form = reactive({ acceptedTerms: false, postalCode: '' })
const recaptcha = useRecaptcha()
const { authentication, consumer } = useApi()
const user = useUserStore()
const locale = useLocale()
const countryCode = useCountryCode()
const { otpLogin, otpSignUp, postLoginActions } = useAuthStore()
const { load: loadRules, getValidator, formatters } = useAddressApiRules()
const appConfig = useAppConfig()
const { postalCode } = appConfig.components.form.signUp
const {
  brandClasses,
  otpHideLabel,
  otpResendCountdown,
  otpResendHelpLink,
  otpShowResendAfterCta,
} = appConfig.components.dialog.simplifiedEnrollment
const id = useId()
const { $gtm } = useNuxtApp()

const codeResent = ref(false)
const hasResentCode = ref(false)
const resendDisabled = ref(!!otpResendCountdown)
const resendCountdown = otpResendCountdown
  ? useCountdown(otpResendCountdown, {
      onComplete() {
        resendDisabled.value = false
      },
    })
  : undefined
const passkeyError = ref(false)
const RESEND_COOLDOWN = 3000
const timer = ref<ReturnType<typeof setTimeout>>()
const { signInWithPasskey } = usePasskey(email, {
  onContinue: (state) => emit('continue', state),
  onError: () => { passkeyError.value = true },
  onSuccess: () => emit('resolve'),
  formLocation
})

const postalCodeFormat = computed(() => formatters.value.postalCode || {})
const postalCodeRequired = computed(() => action === 'signup' && postalCode?.requiredLocales?.includes(locale))

const completedDigits = computed(() => otpDigits.value.reduce((acc: number[], digit, i) => [
  ...acc,
  ...(!Number.isNaN(digit) ? [i] : [])
], []))

const isComplete = computed(() => otpDigits.value.length === digits
  && otpDigits.value.every((v) => Number.isInteger(+v)))

const otpSignIn = async (otpCode: string) => {
  try {
    loading.value = true

    if (action === 'signup') $gtm.push('authForm.onRegistrationSubmit', formLocation, 'ecom')
    $gtm.push('authForm.onLoginSubmit', formLocation, action === 'signup')

    const captchaResponse = await recaptcha.execute('login')

    switch (action) {
      case 'login':
        await otpLogin(email, otpCode, captchaResponse)
        break

      case 'signup':
        if (formRef.value?.validate(true))
          await otpSignUp(email, otpCode, captchaResponse, form.postalCode)
        break
    }

    await user.get()
    await postLoginActions().catch((err) => log.error(err))

    if (action === 'signup') $gtm.push('authForm.onRegistrationConfirmed', 'Confirmed', formLocation, 'ecom', 'OTP', false)
    $gtm.push('authForm.onLoginConfirmed', formLocation, 'OTP', action === 'signup')

    emit('continue', EnrollmentState.COMPLETE_PROFILE_SUBSCRIPTION)
  }
  catch {
    error.value = true

    if (action === 'signup')
      $gtm.push('authForm.onAuthFlowInteraction', 'Registration', 'Error', formLocation, 'OTP')
  }
  finally {
    loading.value = false
  }
}

const createPasskey = async (otpCode: string) => {
  const { codeChallenge } = await getPkceCodePair()
  const captchaResponse = await recaptcha.execute('login')

  const response = await authentication.$mfa({
    authMethod: 'passkey',
    action: 'startRegistration',
    username: email,
    codeChallenge,
    recaptcha_response: captchaResponse,
    pwd_action_token: otpCode
  }) as Extract<MfaData, { challenge: string }>

  try {
    const credential = await navigator.credentials.create({
      publicKey: {
        ...response,
        challenge: base64URLToBuffer(response.challenge),
        excludeCredentials: (response.excludeCredentials ?? []).map((cred) => ({
          ...cred,
          id: base64URLToBuffer(cred.id)
        })),
        user: {
          ...response.user,
          id: base64URLToBuffer(response.user.id)
        }
      }
    }) as PublicKeyCredential | null

    if (!credential) {
      throw new Error('Credential creation was not completed.')
    }

    const credentialResponse = credential.response as AuthenticatorAttestationResponse

    await authentication.$mfa({
      authMethod: 'passkey',
      action: 'finishRegistration',
      username: email,
      codeChallenge: (await getPkceCodePair()).codeChallenge,
      recaptcha_response: await recaptcha.execute('login'),
      pwd_action_token: otpCode,
      credential: {
        id: credential.id,
        type: credential.type,
        clientDataJSON: bufferToBase64URL(credentialResponse.clientDataJSON),
        attestationObject: bufferToBase64URL(credentialResponse.attestationObject)
      }
    })

    await signInWithPasskey()
  }
  catch (err) {
    passkeyError.value = true
    throw err
  }
}

const handleVerify = async () => {
  error.value = false
  passkeyError.value = false

  const otpCode = otpDigits.value.join('')
  if (otpCode.length !== digits) return false

  if (method === 'passkey') {
    try {
      await createPasskey(otpCode)
    }
    catch (err) {
      log.error(err)

      if (!passkeyError.value)
        error.value = true
    }
  }
  else {
    await otpSignIn(otpCode)
  }
}

const resendOtp = async () => {
  if (resendDisabled.value) return

  if (action === 'signup')
    $gtm.push('authForm.onAuthFlowInteraction', 'Registration', 'ResendCode', formLocation, 'OTP')

  try {
    resendDisabled.value = true
    codeResent.value = true
    hasResentCode.value = true
    const captchaResponse = await recaptcha.execute('login')

    if (action === 'login') {
      const { codeChallenge } = await getPkceCodePair()

      await authentication.$mfa({
        ...method === 'passkey' ? { authMethod: method, action: 'authorization' } : { authMethod: method },
        username: email,
        codeChallenge,
        recaptcha_response: captchaResponse,
      })
    }

    if (action === 'signup') {
      await consumer.$lookup({ email, recaptcha_response: captchaResponse })
    }

    timer.value = setTimeout(() => {
      codeResent.value = false
      resendDisabled.value = false
      resendCountdown?.start()
    }, RESEND_COOLDOWN)
  }
  catch {
    resendDisabled.value = false
    codeResent.value = false
  }
}

onMounted(() => {
  if (postalCodeRequired.value)
    loadRules(countryCode.toLocaleLowerCase(), locale)

  resendCountdown?.start()
})

onBeforeUnmount(() => clearTimeout(timer.value))
</script>
